import React, { useEffect, useState } from 'react';
import useImageCrop from '../hooks/useImageCrop';
import { User, Save, Loader2, Camera, Lock, ReceiptText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../supabase/supabase';
import { EMPTY_BILLING, loadBillingProfile, saveBillingProfile } from '../utils/billingProfile';
import BillingFields from '../components/BillingFields';

const Profile = () => {
  const { user, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState(EMPTY_BILLING);
  const [formData, setFormData] = useState({ name: user?.name || '', crm: user?.crm || '', uf: user?.uf || '', specialty: user?.specialty || '', type: user?.type || 'medico' });

  useEffect(() => {
    if (!user?.id) return;
    loadBillingProfile(user.id).then(setBilling).catch(() => {});
  }, [user?.id]);

  const isVet = formData.type === 'veterinario';
  const docLabel = isVet ? 'CRMV' : 'CRM';

  const handleUpdate = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { error } = await supabase.from('users').update(formData).eq('id', user.id);
      if (error) throw error;
      const savedBilling = await saveBillingProfile(user.id, billing);
      setBilling(savedBilling);
      await refreshProfile();
      alert('Perfil atualizado com sucesso!');
    } catch (err) { alert(err.message); } finally { setLoading(false); }
  };

  const { cropImages, cropModal } = useImageCrop();

  const handleAvatarUpload = async (e) => {
    const picked = e.target.files[0]; e.target.value = ''; if (!picked) return;
    const cropped = await cropImages([picked]); if (!cropped) return;
    const [file] = cropped; setLoading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/avatar-${crypto.randomUUID()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('report-images').upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('report-images').getPublicUrl(fileName);
      await supabase.from('users').update({ avatar_url: publicUrl }).eq('id', user.id);
      await refreshProfile();
    } catch (err) { alert(err.message); } finally { setLoading(false); }
  };

  return <div className="max-w-4xl mx-auto px-4 py-12">
    {cropModal}
    <h1 className="mb-12 font-display text-3xl font-bold text-navy tracking-tight">Seu perfil</h1>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-1 space-y-8">
        <div className="bg-paper p-8 rounded-card border border-line shadow-card flex flex-col items-center">
          <div className="relative group mb-6"><div className="w-32 h-32 rounded-card bg-paper-alt border-2 border-dashed border-line overflow-hidden flex items-center justify-center">{user?.avatar_url ? <img src={user.avatar_url} alt="Foto de perfil" className="w-full h-full object-cover" /> : <User className="w-12 h-12 text-muted" />}</div><label className="absolute inset-0 flex items-center justify-center bg-navy/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-card cursor-pointer"><Camera className="text-white w-8 h-8" /><input type="file" onChange={handleAvatarUpload} className="hidden" accept="image/*" /></label></div>
          <h2 className="font-display text-lg font-bold text-ink text-center">{user?.name}</h2><p className="text-[10px] font-semibold text-muted uppercase tracking-[0.2em] mt-2">Plano {user?.plan_tier}</p>
        </div>
        <div className="bg-paper p-8 rounded-card border border-line shadow-card"><h3 className="mb-6 text-xs font-semibold text-ink uppercase tracking-widest">Segurança</h3><div className="p-4 bg-paper-alt rounded-card"><p className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1">Email</p><p className="text-xs font-medium text-ink font-mono">{user?.email}</p></div></div>
      </div>
      <div className="lg:col-span-2">
        <form onSubmit={handleUpdate} className="space-y-8">
          <div className="bg-paper p-10 rounded-card border border-line shadow-card space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2"><label className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">Nome completo</label><input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="input-field" /></div>
              <div><label className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">Tipo de profissional</label><select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})} className="input-field appearance-none"><option value="medico">Médico (CRM)</option><option value="veterinario">Veterinário (CRMV)</option></select></div>
              <div><label className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">Especialidade</label><input type="text" value={formData.specialty} onChange={e => setFormData({...formData, specialty: e.target.value})} className="input-field" /></div>
              <div><label className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">{docLabel}</label><input type="text" value={formData.crm} onChange={e => setFormData({...formData, crm: e.target.value})} className="input-field font-mono" /></div>
              <div><label className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">UF de registro</label><input type="text" maxLength={2} value={formData.uf} onChange={e => setFormData({...formData, uf: e.target.value.toUpperCase()})} className="input-field font-mono" /></div>
            </div>
          </div>
          <div className="bg-paper p-10 rounded-card border border-line shadow-card space-y-6">
            <div><h2 className="font-display text-xl font-bold text-ink">Dados de cobrança</h2><p className="text-xs text-muted mt-1">Usados para pré-preencher Pix Automático e cartão.</p></div>
            <BillingFields billing={billing} setBilling={setBilling} />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full uppercase tracking-widest text-xs">{loading ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Salvando" /> : 'Salvar alterações'}</button>
        </form>
      </div>
    </div>
  </div>;
};

export default Profile;
